import type {
  LightingAdvisorInput,
  LightingAdvisorNote,
  LightingAdvisorQuestion,
  LightingAnswers,
  LightingAnswerValue
} from '~/types/lightingAdvisor';
import { ALL_LIGHT_TYPE_VALUES } from '~/utils/lightingAdvisor/constants';

export function getAllowedLightTypes(
  a2s1q1: LightingAnswerValue,
  a2s1q2: LightingAnswerValue
): string[] {
  const typeSet = [...ALL_LIGHT_TYPE_VALUES];
  let toBeRemoved: string[] = [];

  switch (a2s1q1) {
    case 'transmitted_light':
      toBeRemoved = [
        'bar_light',
        'coaxial_light',
        'dark_field_light',
        'dome_light',
        'flat_dome_light',
        'ring_light',
        'spot_light'
      ];
      break;
    case 'dark_field':
      toBeRemoved = [
        'back_light',
        'coaxial_light',
        'dome_light',
        'flat_dome_light',
        'ring_light'
      ];
      break;
    case 'incident_light':
      switch (a2s1q2) {
        case 'low_reflection':
          toBeRemoved = [
            'back_light',
            'coaxial_light',
            'dark_field_light',
            'dome_light',
            'flat_dome_light'
          ];
          break;
        case 'medium_reflection':
          toBeRemoved = [
            'back_light',
            'coaxial_light',
            'dark_field_light',
            'dome_light',
            'spot_light'
          ];
          break;
        case 'strong_reflection':
          toBeRemoved = [
            'back_light',
            'bar_light',
            'dark_field_light',
            'ring_light',
            'spot_light'
          ];
          break;
        default:
          toBeRemoved = [...typeSet];
      }
      break;
    default:
      toBeRemoved = [...typeSet];
  }

  return typeSet.filter((value) => !toBeRemoved.includes(value));
}

export function isQuestionVisible(
  questionId: string,
  answers: LightingAnswers
): boolean {
  switch (questionId) {
    case 'a2s1q2':
      return answers.a2s1q1 === 'incident_light';
    case 'a2s1q3':
      return getAllowedLightTypes(answers.a2s1q1, answers.a2s1q2).length > 0;
    case 'a2s3q2':
      return answers.a2s3q1 === 'colored' || answers.a2s3q1 === 'infrared';
    case 'a2s3q3':
      return (
        answers.a2s3q1 === 'infrared' &&
        answers.a2s3q2 != null &&
        answers.a2s3q2 !== ''
      );
    default:
      return true;
  }
}

function filterA2s3q2Options(
  a2s3q1: LightingAnswerValue,
  options: ReadonlyArray<LightingAdvisorInput>
): LightingAdvisorInput[] {
  if (a2s3q1 === 'white') {
    return [];
  }
  if (a2s3q1 === 'colored') {
    return options.filter((option) =>
      ['blue_green_features', 'red_yellow_features'].includes(option.value)
    );
  }
  return options.filter((option) =>
    ['infrared', 'swir', 'uv'].includes(option.value)
  );
}

function filterA2s3q3Options(
  a2s3q2: LightingAnswerValue,
  options: ReadonlyArray<LightingAdvisorInput>
): LightingAdvisorInput[] {
  if (a2s3q2 === 'uv') {
    return options.filter((option) =>
      ['uv_365nm', 'uv_395nm', 'uv_396nm'].includes(option.value)
    );
  }
  if (a2s3q2 === 'infrared') {
    return options.filter((option) => option.value === 'ir_850nm');
  }
  return options.filter((option) =>
    [
      'swir_1050nm',
      'swir_1200nm',
      'swir_1300nm',
      'swir_1450nm',
      'swir_1550nm',
      'swir_1650nm'
    ].includes(option.value)
  );
}

export function getFilteredQuestionInputs(
  question: LightingAdvisorQuestion,
  answers: LightingAnswers
): LightingAdvisorInput[] {
  const options = question.inputs ?? [];

  if (question.id === 'a2s1q3') {
    const allowed = getAllowedLightTypes(answers.a2s1q1, answers.a2s1q2);
    return options.filter((option) => allowed.includes(option.value));
  }

  if (question.id === 'a2s3q2') {
    return filterA2s3q2Options(answers.a2s3q1, options);
  }

  if (question.id === 'a2s3q3') {
    return filterA2s3q3Options(answers.a2s3q2, options);
  }

  return [...options];
}

export function cascadeResetAnswers(
  answers: LightingAnswers,
  changedId: string
): LightingAnswers {
  const next: LightingAnswers = { ...answers };

  if (changedId === 'a2s1q1') {
    if (next.a2s1q1 !== 'incident_light') {
      next.a2s1q2 = null;
    }
    const allowed = getAllowedLightTypes(next.a2s1q1, next.a2s1q2);
    if (next.a2s1q3 && !allowed.includes(String(next.a2s1q3))) {
      next.a2s1q3 = null;
    }
  }

  if (changedId === 'a2s1q2') {
    const allowed = getAllowedLightTypes(next.a2s1q1, next.a2s1q2);
    if (next.a2s1q3 && !allowed.includes(String(next.a2s1q3))) {
      next.a2s1q3 = null;
    }
  }

  if (changedId === 'a2s3q1') {
    next.a2s3q2 = null;
    next.a2s3q3 = null;
  }

  if (changedId === 'a2s3q2') {
    next.a2s3q3 = null;
  }

  if (changedId === 'a2s5q2') {
    next.a2s5q2_basler_series = null;
    next.a2s5q2_basler_camera = null;
    next.a2s5q2_thirdparty_mount = null;
    next.a2s5q2_thirdparty_monocolor = null;
  }

  if (changedId === 'a2s5q2_basler_series') {
    next.a2s5q2_basler_camera = null;
  }

  return next;
}

export interface ResolveQuestionNotesContext {
  allowedLightTypes?: string[];
  a2s3q1?: LightingAnswerValue;
}

export function resolveQuestionNotes(
  question: LightingAdvisorQuestion,
  context: ResolveQuestionNotesContext = {}
): LightingAdvisorNote[] {
  const notes = question.notes ?? [];
  const allowedLightTypes = context.allowedLightTypes ?? [];
  const a2s3q1 = context.a2s3q1 ?? null;

  if (question.id === 'a2s3q2') {
    if (a2s3q1 === 'colored') {
      const colored = notes.find((note) => note.type === 'tooltip_colored');
      return colored ? [colored] : [];
    }
    if (a2s3q1 === 'infrared') {
      const infrared = notes.find((note) => note.type === 'tooltip_infrared');
      return infrared ? [infrared] : [];
    }
    return [];
  }

  return notes.filter((note) => {
    if (note.type === 'warning_content') {
      return Boolean(note.text);
    }
    if (note.type === 'tooltip') {
      const hasContent = Boolean(note.text || note.textKey);
      if (!note.requirement) {
        return hasContent;
      }
      return allowedLightTypes.includes(note.requirement) && hasContent;
    }
    return false;
  });
}

/**
 * Dictionary keys backing a question's info modal. The modal renders these as
 * Rich Text so embedded entries (images, text-media) are displayed.
 */
export function getQuestionTooltipKeys(
  question: LightingAdvisorQuestion,
  context: ResolveQuestionNotesContext = {}
): string[] {
  return resolveQuestionNotes(question, context)
    .filter((note) => note.type !== 'warning_content')
    .map((note) => note.textKey)
    .filter((key): key is string => Boolean(key));
}

export function getWarningNotesForQuestion(
  question: LightingAdvisorQuestion,
  answers: LightingAnswers,
  context: ResolveQuestionNotesContext = {}
): LightingAdvisorNote[] {
  if (question.id === 'a2s3q1') {
    // Only surface the eye-safety warning once the user picked one of the
    // a2s3q1 options (white / colored / infrared).
    if (!answers.a2s3q1) {
      return [];
    }
    const warning = question.notes?.find(
      (note) => note.type === 'warning_content'
    );
    return warning ? [warning] : [];
  }
  return resolveQuestionNotes(question, context).filter(
    (note) => note.type === 'warning_content'
  );
}

export function buildQueryFromAnswers(
  answers: LightingAnswers,
  questions: LightingAdvisorQuestion[]
): Record<string, string> {
  const query: Record<string, string> = {};
  const questionById = new Map(questions.map((q) => [q.id, q]));

  for (const question of questions) {
    if (!isQuestionVisible(question.id, answers)) {
      continue;
    }

    if (question.id === 'a2s2q2') {
      const length = answers.a2s2q2_length;
      const width = answers.a2s2q2_width;
      if (length != null && length !== '') {
        query.a2s2q2_length = String(length);
      }
      if (width != null && width !== '') {
        query.a2s2q2_width = String(width);
      }
      continue;
    }

    const value = answers[question.id];
    if (value != null && value !== '') {
      query[question.id] = String(value);
    }
  }

  // Drop select answers that are no longer valid after dependency changes
  for (const [id, question] of questionById) {
    if (!isQuestionVisible(id, answers)) {
      continue;
    }
    if (id === 'a2s2q1' || id === 'a2s2q2') {
      continue;
    }
    const filtered = getFilteredQuestionInputs(question, answers);
    const current = answers[id];
    if (
      current != null &&
      current !== '' &&
      filtered.length > 0 &&
      !filtered.some((input) => input.value === current)
    ) {
      delete query[id];
    }
  }

  return query;
}

export function normalizeAnswersAfterLoad(
  answers: LightingAnswers
): LightingAnswers {
  let next = { ...answers };
  next = cascadeResetAnswers(next, 'a2s1q1');
  next = cascadeResetAnswers(next, 'a2s1q2');

  const allowedSpectrums =
    next.a2s3q1 === 'colored'
      ? ['blue_green_features', 'red_yellow_features']
      : next.a2s3q1 === 'white'
        ? []
        : ['infrared', 'swir', 'uv'];
  if (next.a2s3q2 && !allowedSpectrums.includes(String(next.a2s3q2))) {
    next.a2s3q2 = null;
    next.a2s3q3 = null;
  }

  const allowedWavelengths =
    next.a2s3q2 === 'uv'
      ? ['uv_365nm', 'uv_395nm', 'uv_396nm']
      : next.a2s3q2 === 'infrared'
        ? ['ir_850nm']
        : next.a2s3q2 === 'swir'
          ? [
              'swir_1050nm',
              'swir_1200nm',
              'swir_1300nm',
              'swir_1450nm',
              'swir_1550nm',
              'swir_1650nm'
            ]
          : [];
  if (next.a2s3q3 && !allowedWavelengths.includes(String(next.a2s3q3))) {
    next.a2s3q3 = null;
  }

  if (next.a2s5q2 === 'basler') {
    next.a2s5q2_thirdparty_mount = null;
    next.a2s5q2_thirdparty_monocolor = null;
  } else if (next.a2s5q2 === 'thirdparty') {
    next.a2s5q2_basler_series = null;
    next.a2s5q2_basler_camera = null;
  } else {
    next.a2s5q2_basler_series = null;
    next.a2s5q2_basler_camera = null;
    next.a2s5q2_thirdparty_mount = null;
    next.a2s5q2_thirdparty_monocolor = null;
  }
  return next;
}
